import { apiDownloadFile, apiRequest } from './api';

export type ReportTemplate = 'pa-leads' | 'sales';

/** One PA case (advocate_cases): the eMerge record, never the Monday mirror. */
export interface LeadReportRow {
  resident: string | null;
  facility: string | null;
  advocate: string | null;
  origin: string | null;
  adopted: boolean;
  assigned_on: string | null;
  /** EXACT = an eMerge assignment; SOURCE_REPORTED = Monday's Date Assigned. */
  assignment_confidence: 'EXACT' | 'SOURCE_REPORTED' | null;
  assigned_in_range: boolean;
  stage: string | null;
  status: string | null;
  disposition: string | null;
  closed_on: string | null;
  /** What wrote the LATEST close: a person's status change, an eMerge rule (named in close_rule), or the board before adoption. */
  close_date_source: 'EMERGE_STATUS_ACT' | 'EMERGE_INTAKE_RULE' | 'EMERGE_DUPLICATE_CLOSE' | 'EMERGE_ALREADY_SOLD_CLOSE' | 'EMERGE_RULE' | 'SOURCE_REPORTED' | null;
  close_rule?: string | null;
  closed_in_range: boolean;
  tags: string[] | string | null;
}

export interface SalesReportRow {
  resident: string | null;
  facility: string | null;
  advocate: string | null;
  sale_closed_at: string | null;
  sale_closed_on: string | null;
  sale_amount: number | string | null;
  amount_source: string | null;
  sale_close_source: string | null;
  sale_close_confidence: string | null;
  assigned_on: string | null;
  assignment_confidence: string | null;
  assignment_origin: string | null;
  credit_basis: string | null;
  enrollment_state: string | null;
  umed_stage: string | null;
  finance_handoff_state: string | null;
  reversed_at: string | null;
  /** SALE_ROW_REVERSED, or UMED_CLAIM_UNWOUND (ruling R2, 2026-10-11). */
  reversal_basis: 'SALE_ROW_REVERSED' | 'UMED_CLAIM_UNWOUND' | null;
  sale_stands: boolean;
}

export interface ReportSummary {
  sales_closed?: number | null;
  sale_value?: number | string | null;
  amount_pending?: number | string | null;
  [key: string]: number | string | null | undefined;
}

export interface DailyReportTotal {
  date: string;
  sales_closed: number;
  sale_value: number | string | null;
  reversed: number;
}

export interface ReportData {
  template: ReportTemplate;
  from: string;
  to: string;
  timezone: string;
  generated_at: string;
  scope: 'SELF' | 'TEAM';
  rows: Array<LeadReportRow | SalesReportRow>;
  summary: ReportSummary;
  daily_totals: DailyReportTotal[];
  device_units: null;
  unit_notice: string | null;
  content_hash: string;
}

export interface ReportingResult<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
  code?: string;
  status?: number;
}

export interface ReportEmailResult {
  status: 'PROVIDER_ACCEPTED';
  row_count: number;
  audit_status?: 'RECORDED' | 'UPDATE_PENDING';
}

/** Which reports the caller may open (GET /emerge/reports/catalog). */
export interface ReportCatalog {
  pa_reports: boolean;
  daily_outcomes: boolean;
  /** Team PA performance dashboard. Absent from a server that predates it: treated as false. */
  pa_performance?: boolean;
}

/** One America/New_York day of the daily approvals & reversals report (services/reporting/dailyOutcomes.js). */
export interface DailyOutcomeDay {
  date: string | null;
  approvals: number;
  /** Approvals made in eMerge (the approval act). */
  approvals_emerge: number;
  /** Approvals dated by Monday's board Approval Date (cases eMerge never approved itself). */
  approvals_board: number;
  /** Reversals DCF approved (completed) that day. */
  reversals: number;
  reversal_total: number;
  reversal_amount_missing: number;
  /** Completed reversals of a claim an earlier reversal already took back: counted, dollars not added again. */
  reversals_already_reversed: number;
  reversals_requested: number;
  /**
   * DCF resubmittals with corrections (2026-10-15): the claim stands at new terms, so never a reversal. Requested by
   * Finance that day, and approved by DCF that day. Absent from a server that predates them.
   */
  resubmittals_requested?: number;
  resubmittals_approved?: number;
}

export interface DailyOutcomesDefinitions {
  approval: string;
  approval_day: string;
  board_history: string;
  once_per_episode: string;
  reversal: string;
  reversal_amount: string;
  board_reversals: string;
  collections: string;
  [key: string]: string;
}

export interface DailyOutcomesMonth {
  month: string;
  from: string;
  to: string;
  timezone: string;
  generated_at: string;
  days: DailyOutcomeDay[];
  totals: DailyOutcomeDay;
  /** One row per PA person (keyed by user id; two advocates may share a name). */
  by_pa: Array<{ pa_user_id: string | null; pa: string | null; approvals: number; approvals_board: number }>;
  undated_board_reversals: number;
  collections: { available: false; reason: string };
  definitions: DailyOutcomesDefinitions;
}

export type ApprovalDateBasis = 'ACT_DAY' | 'STATED_DAY' | 'BOARD_DATE';

export interface DailyApprovalItem {
  kind: 'APPROVAL';
  umed_case_id: string;
  resident: string | null;
  building: string | null;
  pa_user_id: string | null;
  pa: string | null;
  pa_basis: 'SALE_CREDIT' | 'CASE_HOLDER' | null;
  date: string;
  date_basis: ApprovalDateBasis;
  approved_at: string | null;
  approved_by: string | null;
  approval_basis: string | null;
  board_approved_on: string | null;
  later_approvals: number;
}

export interface DailyReversalItem {
  kind: 'REVERSAL_COMPLETED' | 'REVERSAL_REQUESTED';
  umed_case_id: string;
  reversal_id: string;
  resident: string | null;
  building: string | null;
  pa: string | null;
  requester: string | null;
  state: string;
  amount: number | null;
  amount_source: string | null;
  /** UMED_CASE_SALE: the reversed UMED case's own sale; PA_CASE_SALE: its PA case's sale (no direct match). */
  amount_basis: 'UMED_CASE_SALE' | 'PA_CASE_SALE' | null;
  sale_id: string | null;
  /** An earlier completed reversal already took this sale back: counted, its amount not added again. */
  already_reversed: boolean;
  requested_on: string | null;
  completed_on: string | null;
  date: string;
}

export interface DailyOutcomesDayDetail {
  date: string;
  timezone: string;
  generated_at: string;
  totals: DailyOutcomeDay;
  approvals: DailyApprovalItem[];
  reversals: DailyReversalItem[];
  definitions: DailyOutcomesDefinitions;
}

/** PA performance dashboard (GET /emerge/reports/pa-performance, 2026-10-19). Counts and PA names only; never a resident. */
export type PerfPeriodKey = 'current' | 'comparison' | 'reference';
export type PerfCompare = 'prior_month' | 'prior_period';

export interface PerfMeasures {
  /** Distinct residents assigned to the PA in the period. */
  leads: number;
  workable: number;
  one_ear_only: number;
  liability_missing: number;
  /** Leads whose assignment date is Monday's (pre-eMerge), not an eMerge act. */
  leads_source_reported: number;
  /** Gross sales closed in the period (includes later-reversed). */
  sales: number;
  sales_reversed: number;
  /** Reversed OR episode ended without approval. */
  sales_not_standing: number;
  sales_net: number;
  sale_value: number;
  sale_value_net: number;
  /** Sales with no amount recorded. */
  amount_pending: number;
  ears_two: number;
  ears_one: number;
  ears_unknown: number;
}

export interface PerfPeriod { key: PerfPeriodKey; from: string; to: string; days: number }

export interface PaPerformanceAdvocate {
  /** null = sales with no credited PA ("No PA credited"). */
  advocate_user_id: string | null;
  advocate: string;
  current: PerfMeasures;
  comparison: PerfMeasures;
  reference: PerfMeasures | null;
}

export interface PaPerformanceTrendRow {
  period: PerfPeriodKey;
  date: string;
  leads: number;
  workable: number;
  sales: number;
  sales_net: number;
  sale_value: number;
}

export interface PaPerformanceData {
  from: string;
  to: string;
  /** Effective end = min(to, today NY); the current period ends here. */
  through: string;
  today: string;
  timezone: 'America/New_York';
  generated_at: string;
  compare: { requested: PerfCompare; applied: PerfCompare; reason: null | 'RANGE_LONGER_THAN_A_MONTH' };
  periods: {
    current: PerfPeriod & { partial_month: boolean };
    comparison: PerfPeriod;
    /** All of the previous month, only when the current period is a partial (month-to-date) month. */
    reference: PerfPeriod | null;
  };
  /** The whole team. Leads are DISTINCT residents, so total leads can be less than the sum of PA rows. */
  totals: Record<PerfPeriodKey, PerfMeasures | null>;
  advocates: PaPerformanceAdvocate[];
  /** One row per NY day that had activity; days with nothing are absent. */
  trend: PaPerformanceTrendRow[];
  definitions: Record<string, string>;
  rulings: Array<{ id: string; question: string; default: string }>;
}

export const reportingApi = {
  catalog: (): Promise<ReportingResult<ReportCatalog>> => apiRequest<ReportCatalog>('GET', '/emerge/reports/catalog'),
  dailyOutcomes: (month: string): Promise<ReportingResult<DailyOutcomesMonth>> =>
    apiRequest<DailyOutcomesMonth>('GET', `/emerge/reports/daily-outcomes?${new URLSearchParams({ month }).toString()}`),
  dailyOutcomesDay: (date: string): Promise<ReportingResult<DailyOutcomesDayDetail>> =>
    apiRequest<DailyOutcomesDayDetail>('GET', `/emerge/reports/daily-outcomes/day?${new URLSearchParams({ date }).toString()}`),
  /** Saves the month table as CSV; resolves to a message to show, or null on success. */
  exportDailyOutcomes: (month: string): Promise<string | null> => apiDownloadFile('/emerge/reports/daily-outcomes/export', { month }),
  paPerformance: (from: string, to: string, compare: PerfCompare): Promise<ReportingResult<PaPerformanceData>> =>
    apiRequest<PaPerformanceData>('GET', `/emerge/reports/pa-performance?${new URLSearchParams({ from, to, compare }).toString()}`),
  /** Saves the PA performance table as CSV; resolves to a message to show, or null on success. */
  exportPaPerformance: (from: string, to: string, compare: PerfCompare): Promise<string | null> =>
    apiDownloadFile('/emerge/reports/pa-performance/export', { from, to, compare }),
  get: (template: ReportTemplate, from: string, to: string): Promise<ReportingResult<ReportData>> => {
    const query = new URLSearchParams({ template, from, to });
    return apiRequest<ReportData>('GET', `/emerge/reports?${query.toString()}`);
  },
  email: (input: { template: ReportTemplate; from: string; to: string; recipient: string; acknowledged_recipient: string; report_hash: string }): Promise<ReportingResult<ReportEmailResult>> => {
    if (process.env.NODE_ENV !== 'production') {
      return Promise.resolve({ success: false, code: 'EMAIL_DISABLED', message: 'Email delivery is disabled outside production.' });
    }
    return apiRequest<ReportEmailResult>('POST', '/emerge/reports/email', { ...input, confirmed: true });
  },
};